import {
  type IoConfig,
  type ParentStyledElementProps,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type CanvasElementPropertiesWithData,
  type ElementSizingMode,
  type FontOptions,
  type PropertiesConfig,
  type PropertiesConfigInputBackground,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import {
  propsConfigHelper as epc,
  relabelSizingMinToFitContent,
} from '../../utils/elements-props-config.helpers';

import { TextAreaDefinitionMapper } from './ai/text-area.definition-mapper';
import { TextAreaElementHandler } from './ai/text-area.element-handler';
import { textAreaV2Migration } from './migrations/002-text-area.migration';
import { textAreaV3Migration } from './migrations/003-text-area.migration';
import { TextAreaComponent } from './text-area.component';
import { TextAreaPropsBuilder } from './text-area.props-builder';

export type TextAreaDef = WithProps<TextAreaPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    textArea: TextAreaDef;
  }
}

const DEFAULT_WIDTH = 356;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full', 'min'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto', 'min'];

registerElementDefinition({
  type: 'textArea',
  componentType: TextAreaComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Text',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: TextAreaPropsBuilder,
  styleIncludePaths: ['style', 'content.font', 'content.container'],
  version: 3,
  migrations: {
    2: textAreaV2Migration,
    3: textAreaV3Migration,
  },
  ai: {
    name: 'textArea',
    handler: () => TextAreaElementHandler,
    definitionMapper: () => TextAreaDefinitionMapper,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Displayed value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  ParentStyledElementProps;

export type TextAreaPropsModel = PropsModel & {
  content: {
    input: {
      value: string;
    };
    font: FontOptions;
    container: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      overflow: string;
    };
  };
  style: {
    general: {
      textColor: PropertiesConfigInputColorModel;
      linkColor: PropertiesConfigInputColorModel;
    };
    background: PropertiesConfigInputBackground;
  };
};

function getDefaults(): PropsOf<'textArea'> {
  return {
    content: {
      input: {
        value: 'Text',
      },
      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontSize: '20px',
        lineHeight: '20px',
        letterSpacing: '0px',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
      container: {
        offset: {
          padding: '$t.spacingInput',
          align: 'middle left',
        },
        overflow: 'auto',
      },
    },
    style: {
      general: {
        textColor: { color1: '$t.title', selectorType: 'solid' },
        linkColor: { color1: '$t.accent', selectorType: 'solid' },
      },
      background: {
        outlineWidth: '0px',
        fill: { color1: '#ffffff', selectorType: 'none' },
        outline: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'textArea'> {
  return {
    content: {
      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        lineHeight: '$t.lineHeight',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('textArea', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content.input', 'Text', [
        epc.utils.dataSourcedField(
          epc.fields.textAreaField('content.input.value', '', { hideLabel: true }),
          [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Displayed value', dataType: 'String' }],
            },
          ],
        ),
      ]),
      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
          relabelSizingMinToFitContent,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
          relabelSizingMinToFitContent,
        ),
        epc.fields.contentOverflowField('content.container.overflow', 'Text behavior'),
      ]),
      epc.subsections.visibilitySubsection(),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('content.font', 'Font', [
          ...epc.fields.fontFamilyAndWeightFields(
            'content.font.fontFamily',
            'content.font.fontWeight',
          ),
          epc.fields.fontStyleField(`content.font.style`, 'Style', ['bold']),
          epc.utils.observedField(
            epc.fields.sliderField(`content.font.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
              return validateLineHeightWithFontSize(props, value, 'content.font.lineHeight');
            },
          ),
          epc.utils.observedField(
            epc.fields.sliderField(`content.font.lineHeight`, 'Line height', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: TextAreaPropsModel, value: ValueWithUnitOfMeasure) => {
              return validateFontSizeWithLineHeight(props, value, 'content.font.fontSize');
            },
          ),
          epc.fields.sliderField(`content.font.letterSpacing`, 'Letter spacing', [
            { min: -10, max: 10, unit: 'px', step: 0.1 },
            { min: -2, max: 2, unit: 'rem', step: 0.1 },
            { min: -15, max: 15, unit: '%', step: 0.1 },
          ]),
        ]),
        epc.subsections.subsection('style.general', 'Text', [
          epc.fields.colorField('style.general.textColor', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.linkColor', 'Links', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.background', 'Background', {
          hideEffectPanel: true,
        }),
        epc.subsections.subsection('content.container', 'Padding & Alignment', [
          epc.fields.spacingAndAlignField('content.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
      ],
      true,
    ),
  ]);
}
